import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalOffer as LocalOfferIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D',
  }
};

const UserProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'products', productId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const productData = { id: docSnap.id, ...docSnap.data() };

        // Verificar que el producto esté activo
        if (!productData.active) {
          setError('Este producto no está disponible');
          return;
        }

        setProduct(productData);
      } else {
        setError('Producto no encontrado');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Error al cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (category) => {
    const categories = {
      'uniforme': 'Uniforme',
      'material': 'Material Escolar',
      'libros': 'Libros',
      'otros': 'Otros'
    };
    return categories[category] || 'Otros';
  };

  const handlePayPalClick = () => {
    if (product?.paypalLink) {
      window.open(product.paypalLink, '_blank');
    }
  };

  const handleGoBack = () => {
    navigate('/portal');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: COLORS.accent }} size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: COLORS.background, py: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton
              onClick={handleGoBack}
              sx={{ mr: 2, color: COLORS.accent }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
              Producto
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: COLORS.background, py: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
          <IconButton
            onClick={handleGoBack}
            sx={{ mr: 2, color: COLORS.accent }}
            size={isMobile ? "small" : "medium"}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: 'bold',
              color: COLORS.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: { xs: 'nowrap', sm: 'normal' }
            }}
          >
            {product?.nombre}
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {/* Imágenes del producto */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: COLORS.surface
              }}
            >
              {product?.imágenes && product.imágenes.length > 0 ? (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <CardMedia
                      component="img"
                      sx={{
                        height: { xs: 250, sm: 350, md: 400 },
                        objectFit: 'cover',
                        width: '100%'
                      }}
                      image={product.imágenes[0]}
                      alt={product.nombre}
                    />
                  </Grid>
                  {product.imágenes.length > 1 && (
                    <Grid item xs={12}>
                      <Box sx={{ p: 2 }}>
                        <Grid container spacing={1}>
                          {product.imágenes.slice(1).map((image, index) => (
                            <Grid item xs={4} key={index}>
                              <CardMedia
                                component="img"
                                sx={{
                                  height: 80,
                                  objectFit: 'cover',
                                  borderRadius: 1
                                }}
                                image={image}
                                alt={`${product.nombre} ${index + 2}`}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Box
                  sx={{
                    height: 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: COLORS.primary + '20'
                  }}
                >
                  <ShoppingCartIcon sx={{ fontSize: 120, color: COLORS.accent, opacity: 0.3 }} />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Información del producto */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                borderRadius: 3,
                backgroundColor: COLORS.surface,
                height: 'fit-content'
              }}
            >
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{ fontWeight: 'bold', mb: 2, color: COLORS.text.primary }}
              >
                {product?.nombre}
              </Typography>

              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3,
                flexWrap: 'wrap'
              }}>
                <Chip
                  icon={<CategoryIcon />}
                  label={getCategoryName(product?.categoría)}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    backgroundColor: COLORS.primary + '40',
                    color: COLORS.text.primary
                  }}
                />
                {product?.destacado && (
                  <Chip
                    icon={<LocalOfferIcon />}
                    label="Destacado"
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      backgroundColor: COLORS.success + '20',
                      color: COLORS.success,
                      fontWeight: 'bold'
                    }}
                  />
                )}
              </Box>

              <Typography
                variant={isMobile ? "h4" : "h3"}
                sx={{ fontWeight: 'bold', color: COLORS.success, mb: 3 }}
              >
                {product?.precio ? `${product.precio.toFixed(2)}€` : 'Precio no disponible'}
              </Typography>

              {product?.descripción && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: COLORS.text.primary }}>
                    Descripción
                  </Typography>
                  <Typography variant="body1" sx={{ color: COLORS.text.secondary, lineHeight: 1.8 }}>
                    {product.descripción}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Información adicional */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {product?.stock !== undefined && (
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InventoryIcon sx={{ color: COLORS.accent }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Stock
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {product.stock > 0 ? `${product.stock} unidades` : 'Agotado'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Tallas disponibles */}
              {product?.tallas && product.tallas.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: COLORS.text.primary }}>
                    Tallas disponibles
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {product.tallas.map((talla, index) => (
                      <Chip
                        key={index}
                        label={talla}
                        variant="outlined"
                        sx={{ borderColor: COLORS.accent, color: COLORS.accent }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Colores disponibles */}
              {product?.colores && product.colores.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: COLORS.text.primary }}>
                    Colores disponibles
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {product.colores.map((color, index) => (
                      <Chip
                        key={index}
                        label={color}
                        variant="outlined"
                        sx={{ borderColor: COLORS.secondary, color: COLORS.secondary }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Botón de PayPal */}
              {product?.paypalLink && (
                <Button
                  variant="contained"
                  size={isMobile ? "medium" : "large"}
                  fullWidth
                  startIcon={!isMobile && <LaunchIcon />}
                  onClick={handlePayPalClick}
                  sx={{
                    backgroundColor: COLORS.accent,
                    color: 'white',
                    py: { xs: 1.2, sm: 1.5 },
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 'bold',
                    '&:hover': { backgroundColor: COLORS.secondary },
                    mt: 2
                  }}
                >
                  {isMobile ? 'Comprar con PayPal' : 'Comprar con PayPal'}
                </Button>
              )}

              {!product?.paypalLink && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Para adquirir este producto, ponte en contacto con la administración del AMPA.
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default UserProductDetail;